import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAtribuirLicenca } from '@/hooks/useSoftwareLicencas';
import { useUsersForAssignment } from '@/hooks/useUsersForAssignment';
import { useAtivos } from '@/hooks/useAtivos';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AtribuirLicencaDialogProps {
  licencaId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AtribuirLicencaDialog({ licencaId, open, onOpenChange }: AtribuirLicencaDialogProps) {
  const atribuirLicenca = useAtribuirLicenca();
  const {
    users,
    isLoading: usersLoading,
    error: usersError,
  } = useUsersForAssignment();
  const { data: ativos } = useAtivos();

  const [usuarioId, setUsuarioId] = useState<string>('');
  const [ativoId, setAtivoId] = useState<string>('');
  const [observacoes, setObservacoes] = useState('');
  const [userPopoverOpen, setUserPopoverOpen] = useState(false);

  const selectedUser = useMemo(() => {
    return users?.find((u) => u.id === usuarioId);
  }, [users, usuarioId]);


  const handleSubmit = async () => {
    if (!usuarioId) return;

    await atribuirLicenca.mutateAsync({
      licencaId,
      usuarioId,
      ativoId: ativoId || undefined,
      observacoes: observacoes || undefined,
    });

    setUsuarioId('');
    setAtivoId('');
    setObservacoes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Atribuir Licença</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="usuario">Usuário *</Label>
            <Popover open={userPopoverOpen} onOpenChange={setUserPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={userPopoverOpen}
                  className="w-full justify-between font-normal"
                  type="button"
                >
                  {selectedUser ? (
                    <span className="truncate">
                      {selectedUser.name} ({selectedUser.email})
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Selecione o usuário</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0 bg-popover z-50" align="start">
                <Command>
                  <CommandInput placeholder="Buscar usuário pelo nome..." />
                  <CommandList>
                    {usersLoading ? (
                      <div className="px-2 py-4 text-sm text-center text-muted-foreground">
                        Carregando usuários...
                      </div>
                    ) : usersError ? (
                      <div className="px-2 py-4 text-sm text-center text-destructive">
                        Erro ao carregar usuários.
                      </div>
                    ) : (
                      <>
                        <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
                        <CommandGroup>
                          {users?.map((user) => (
                            <CommandItem
                              key={user.id}
                              value={`${user.name} ${user.email}`}
                              onSelect={() => {
                                setUsuarioId(user.id);
                                setUserPopoverOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  usuarioId === user.id ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              <div className="flex flex-col">
                                <span>{user.name}</span>
                                <span className="text-xs text-muted-foreground">{user.email}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ativo">Ativo/Máquina (opcional)</Label>
            <Select value={ativoId} onValueChange={setAtivoId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o ativo" />
              </SelectTrigger>
              <SelectContent>
                {ativos?.map((ativo) => (
                  <SelectItem key={ativo.id} value={ativo.id}>
                    {ativo.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Observações sobre a atribuição..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            type="button"
            onClick={handleSubmit} 
            disabled={!usuarioId || atribuirLicenca.isPending}
          >
            {atribuirLicenca.isPending ? 'Atribuindo...' : 'Atribuir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
